/**
 * Documents Tab (Shared between Admin and Process Owner)
 * 
 * Displays a 2-column grid of document cards for the selected process owner
 * Read-only view - no upload/delete capabilities
 * Uses Redux for state management and caching
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Dropdown, Input, message } from 'antd';
import { DownloadOutlined, FileWordOutlined, FileExcelOutlined, FilePdfOutlined, FileOutlined, SearchOutlined, LoadingOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import ChevronDownIcon from '../../../../../assets/icons/ic-chevron-down.svg?react';
import { RootState, AppDispatch } from '../../../../../store';
import { setDocuments } from '../../../../../store/slices/certificationSlice';
import {
  getCertificationDocuments,
  formatFileSize,
  type CertificationDocument,
  type ProcessOwner,
} from '../../../../../services/certificationService';
import { downloadFile } from '../../../../../services/fileService';

// ========================================
// Props Interface
// ========================================

interface DocumentsTabProps {
  processOwnerId: string | null;
  isAdmin: boolean;
  selectedProcessOwner?: ProcessOwner | null;
  processOwners?: ProcessOwner[];
  onProcessOwnerChange?: (ownerId: string) => void;
}

// ========================================
// Styled Components
// ========================================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const HeaderControls = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

// Process Owner Chip (from RiskAssessmentDetailPage style)
const ProcessOwnerChip = styled.div<{ $clickable?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  height: 38px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 18px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.005em;
  cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
  transition: all 0.2s;
  
  ${({ $clickable }) => $clickable && `
    &:hover {
      opacity: 0.8;
    }
  `}
`;

const ChevronIcon = styled.div`
  display: flex;
  align-items: center;
  color: #1E40AF;
  
  svg {
    width: 14px;
    height: 14px;
  }
  
  svg path {
    stroke: #1E40AF;
  }
`;

// Dropdown styles
const DropdownMenu = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  padding: 8px;
  width: 280px;
  max-height: 320px;
  overflow-y: auto;
`;

const SearchInput = styled(Input)`
  margin-bottom: 8px;
  border-radius: 6px;
  height: 38px;
  
  .ant-input {
    font-family: Inter;
    font-size: 14px;
  }
`;

const DropdownItem = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$selected ? '#EFF6FF' : 'white'};

  &:hover {
    background: ${props => props.$selected ? '#EFF6FF' : '#F3F4F6'};
  }
`;

const DropdownItemAvatar = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #EFF6FF;
  border-radius: 50%;
  font-family: Inter;
  font-weight: 600;
  font-size: 12px;
  color: #1E40AF;
  flex-shrink: 0;
`;

const DropdownItemText = styled.span<{ $selected?: boolean }>`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: ${props => props.$selected ? '#2563EB' : '#1F2937'};
  flex: 1;
`;

const DocumentsWrapper = styled.div`
  background: #E0E7FF;
  border-radius: 6px;
  border: 1px solid #E0E7FF;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  padding: 2px;
`;

const DocumentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  padding: 16px;
  background: #FFFFFF;
  border-radius: 4px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DocumentCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  transition: all 0.2s;
  
  &:hover {
    border-color: #2563EB;
    box-shadow: 0 2px 8px rgba(37, 99, 235, 0.1);
  }
`;

const DocumentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

const FileIcon = styled.div<{ $type: string }>`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 20px;
  
  ${({ $type }) => {
    switch ($type) {
      case 'pdf':
        return `
          background: #FEE2E2;
          color: #DC2626;
        `;
      case 'doc':
      case 'docx':
        return `
          background: #DBEAFE;
          color: #2563EB;
        `;
      case 'xls':
      case 'xlsx':
        return `
          background: #D1FAE5;
          color: #059669;
        `;
      default:
        return `
          background: #F3F4F6;
          color: #6B7280;
        `;
    }
  }}
`;

const FileDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const FileName = styled.div`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #1F2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileSize = styled.div`
  font-family: Inter;
  font-size: 12px;
  color: #9CA3AF;
  margin-top: 2px;
`;

const DownloadButton = styled.button<{ $loading?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: #F3F4F6;
  border: none;
  border-radius: 8px;
  color: #6B7280;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background: #E5E7EB;
    color: #2563EB;
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
  
  ${({ $loading }) => $loading && `
    color: #2563EB;
  `}
`;

const SpinIcon = styled(LoadingOutlined)`
  font-size: 16px;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  min-height: 400px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #E5E7EB;
    border-top-color: #2563EB;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const EmptyStateWrapper = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 6px;
`;

const EmptyStateText = styled.div`
  color: #1F2937;
  font-family: Inter;
  font-size: 15px;
  font-weight: 500;
`;

// ========================================
// Component
// ========================================

const DocumentsTab: React.FC<DocumentsTabProps> = ({ 
  processOwnerId, 
  isAdmin,
  selectedProcessOwner,
  processOwners = [],
  onProcessOwnerChange,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState(true);
  const [ownerSearchTerm, setOwnerSearchTerm] = useState('');
  const [ownerDropdownOpen, setOwnerDropdownOpen] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  
  // Track last fetched params to prevent duplicate calls
  const lastFetchedRef = useRef<string | null>(null);
  
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  const { documents } = useSelector((state: RootState) => state.certification);
  
  // Get effective process owner ID
  const effectiveProcessOwnerId = selectedProcessOwner?.id || processOwnerId;
  
  // Fetch documents
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!selectedReport || !effectiveProcessOwnerId) {
        setLoading(false);
        return;
      }
      
      // Check cache key to prevent duplicate calls
      const cacheKey = `${selectedReport.annualReportId}-${effectiveProcessOwnerId}`;
      if (lastFetchedRef.current === cacheKey) {
        // console.log('⏭️ DocumentsTab: Skipping duplicate fetch for:', cacheKey);
        setLoading(false);
        return;
      }
      
      lastFetchedRef.current = cacheKey;
      setLoading(true);
      
      try {
        // console.log('📁 DocumentsTab: Fetching documents...', {
        //   annualReportId: selectedReport.annualReportId,
        //   processOwnerId: effectiveProcessOwnerId,
        // });
        
        const docs = await getCertificationDocuments(
          selectedReport.annualReportId,
          effectiveProcessOwnerId
        );
        
        // Update Redux store
        dispatch(setDocuments(docs));
        // console.log('✅ DocumentsTab: Documents loaded:', docs.length);
      } catch (error) {
        console.error('Error fetching documents:', error);
        message.error('Failed to load documents');
        dispatch(setDocuments([]));
      } finally {
        setLoading(false);
      }
    };
    
    fetchDocuments();
  }, [selectedReport, effectiveProcessOwnerId, dispatch]);
  
  // Get file icon based on type
  const getFileIcon = (fileType: CertificationDocument['fileType']) => {
    switch (fileType) {
      case 'pdf':
        return <FilePdfOutlined />;
      case 'doc':
      case 'docx':
        return <FileWordOutlined />;
      case 'xls':
      case 'xlsx':
        return <FileExcelOutlined />;
      default:
        return <FileOutlined />;
    }
  };
  
  // Handle download using fileService
  const handleDownload = useCallback(async (doc: CertificationDocument) => {
    try {
      setDownloadingId(doc.id);
      // console.log('📥 Downloading file:', doc.fileName, 'ID:', doc.id);
      
      await downloadFile(doc.id, doc.fileName);
      
      message.success(`Downloaded ${doc.fileName}`);
    } catch (error) {
      console.error('Download failed:', error);
      message.error(`Failed to download ${doc.fileName}`);
    } finally {
      setDownloadingId(null);
    }
  }, []);
  
  // Filter process owners for search
  const filteredOwners = ownerSearchTerm
    ? processOwners.filter(po => 
        po.processOwnerName.toLowerCase().includes(ownerSearchTerm.toLowerCase())
      )
    : processOwners;
  
  // Get owner initials for avatar
  const getOwnerInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };
  
  // Get display name for process owner
  const getProcessOwnerDisplayName = () => {
    return selectedProcessOwner?.processOwnerName || '';
  };
  
  // Process Owner dropdown content
  const ownerDropdownContent = (
    <DropdownMenu onClick={(e) => e.stopPropagation()}>
      <SearchInput
        prefix={<SearchOutlined />}
        placeholder="Search process owner..."
        value={ownerSearchTerm}
        onChange={(e) => setOwnerSearchTerm(e.target.value)}
        autoFocus
      />
      {filteredOwners.map(po => {
        const isSelected = selectedProcessOwner?.id === po.id;
        return (
          <DropdownItem
            key={po.id}
            $selected={isSelected}
            onClick={() => {
              onProcessOwnerChange?.(po.id);
              setOwnerSearchTerm('');
              setOwnerDropdownOpen(false);
              // Reset cache to trigger refetch for new owner
              lastFetchedRef.current = null;
            }}
          >
            <DropdownItemAvatar>
              {getOwnerInitials(po.processOwnerName)}
            </DropdownItemAvatar>
            <DropdownItemText $selected={isSelected}>
              {po.processOwnerName}
            </DropdownItemText>
          </DropdownItem>
        );
      })}
      {filteredOwners.length === 0 && (
        <div style={{ padding: '16px', textAlign: 'center', color: '#9CA3AF', fontSize: 14 }}>
          No process owners found
        </div>
      )}
    </DropdownMenu>
  );
  
  if (loading) {
    return (
      <LoadingContainer>
        <div className="spinner" />
      </LoadingContainer>
    );
  }
  
  return (
    <Container>
      {/* Header Controls */}
      <HeaderControls>
        {/* Process Owner Chip - Admin with dropdown, PO read-only */}
        {isAdmin && selectedProcessOwner && onProcessOwnerChange ? (
          <Dropdown
            dropdownRender={() => ownerDropdownContent}
            trigger={['click']}
            placement="bottomRight"
            open={ownerDropdownOpen}
            onOpenChange={setOwnerDropdownOpen}
          >
            <ProcessOwnerChip $clickable>
              Process Owner: {getProcessOwnerDisplayName()}
              <ChevronIcon>
                <ChevronDownIcon />
              </ChevronIcon>
            </ProcessOwnerChip>
          </Dropdown>
        ) : (
          <ProcessOwnerChip>
            Process Owner: {getProcessOwnerDisplayName()}
          </ProcessOwnerChip>
        )}
      </HeaderControls>
      
      {/* Documents Grid or Empty State */}
      {documents.length === 0 ? (
        <EmptyStateWrapper>
          <EmptyStateText>
            {isAdmin
              ? "No documents found for this process owner in the current fiscal year."
              : "No documents found for your assigned processes in the current fiscal year."
            }
          </EmptyStateText>
        </EmptyStateWrapper>
      ) : (
        <DocumentsWrapper>
          <DocumentsGrid>
            {documents.map((doc) => (
              <DocumentCard key={doc.id}>
                <DocumentInfo>
                  <FileIcon $type={doc.fileType}>
                    {getFileIcon(doc.fileType)}
                  </FileIcon>
                  <FileDetails>
                    <FileName title={doc.fileName}>{doc.fileName}</FileName>
                    <FileSize>{formatFileSize(doc.fileSize)}</FileSize>
                  </FileDetails>
                </DocumentInfo>
                <DownloadButton 
                  onClick={() => handleDownload(doc)}
                  disabled={downloadingId === doc.id}
                  $loading={downloadingId === doc.id}
                >
                  {downloadingId === doc.id ? <SpinIcon /> : <DownloadOutlined />}
                </DownloadButton>
              </DocumentCard>
            ))}
          </DocumentsGrid>
        </DocumentsWrapper>
      )}
    </Container>
  );
};

export default DocumentsTab;
